import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Image,
  FlatList,
  TextInput,
  KeyboardAvoidingView,
  Platform,
  ActivityIndicator,
  Alert,
} from 'react-native';
import { ms, s, vs } from '../../utils/responsive';
import { SafeAreaView } from 'react-native-safe-area-context';
import { onboardingService } from '../../services/onboarding';
import { analytics } from '../../services/analytics/analytics';
import { AnalyticsEvent } from '../../services/analytics/events';

import { createLogger } from '../../utils/logger';
const logger = createLogger('AIOnboardingChat');

interface Message {
  id: string;
  text: string;
  sender: 'orion' | 'user';
  timestamp: Date;
}

export const AIOnboardingChatScreen = ({ navigation, route }: any) => {
  const mode = route?.params?.mode || 'quick_start';
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputText, setInputText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 5 });
  const [isOnboardingComplete, setIsOnboardingComplete] = useState(false);
  const flatListRef = useRef<FlatList>(null);

  useEffect(() => {
    // Initialize with welcome message from Orion
    initializeConversation();
  }, []);

  const initializeConversation = async () => {
    try {
      // Start onboarding on the backend
      await onboardingService.startOnboarding(mode);

      // Set initial progress based on mode
      const total = mode === 'comprehensive' ? 15 : 5;
      setProgress({ current: 0, total });

      const welcomeMessage: Message = {
        id: '1',
        text: "Great! Let's get started. I'll ask you a few questions to understand your situation better. Feel free to answer naturally - just like talking to a friend.",
        sender: 'orion',
        timestamp: new Date(),
      };
      setMessages([welcomeMessage]);

      // Get first question from AI by sending an empty prompt
      setTimeout(async () => {
        await getFirstQuestion();
      }, 1500);
    } catch (error) {
      logger.error('Failed to initialize onboarding:', error);
      Alert.alert('Error', 'Failed to start onboarding. Please try again.');
    }
  };

  const getFirstQuestion = async () => {
    setIsLoading(true);
    try {
      // Send a prompt to get the first question
      const response = await onboardingService.sendMessage([
        {
          role: 'user',
          content: "I'm ready to get started!",
        },
      ]);

      const question: Message = {
        id: Date.now().toString(),
        text: response.message,
        sender: 'orion',
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, question]);
      setProgress(response.progress);
    } catch (error) {
      logger.error('Failed to get first question:', error);
      Alert.alert('Error', 'Failed to get question. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSend = async () => {
    if (inputText.trim() === '') return;

    // Add user message
    const userMessage: Message = {
      id: Date.now().toString(),
      text: inputText.trim(),
      sender: 'user',
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    const userText = inputText.trim();
    setInputText('');
    setIsLoading(true);

    try {
      // Build conversation history for backend
      const conversationHistory = messages.map(msg => ({
        role: msg.sender === 'orion' ? 'assistant' : 'user',
        content: msg.text,
      }));

      // Add the new user message
      conversationHistory.push({
        role: 'user',
        content: userText,
      });

      // Send to backend
      const response = await onboardingService.sendMessage(conversationHistory);

      // Add AI response
      const aiResponse: Message = {
        id: (Date.now() + 1).toString(),
        text: response.message,
        sender: 'orion',
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, aiResponse]);
      setProgress(response.progress);

      // Check if onboarding is complete
      if (response.progress.current >= response.progress.total) {
        setTimeout(() => {
          handleComplete();
        }, 1500);
      }
    } catch (error: any) {
      logger.error('Failed to send message:', error);
      Alert.alert('Error', error.message || 'Failed to send message. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleComplete = () => {
    analytics.track(AnalyticsEvent.AiOnboardingChatCompleted, {
      mode,
      answered: progress.current,
      total: progress.total,
    });
    const completeMessage: Message = {
      id: Date.now().toString(),
      text: "Perfect! I have everything I need. Based on what you've shared, I've created a personalized task list for you.\n\nYou're all set to start organizing your information. Tap the button below to continue to your profile setup!",
      sender: 'orion',
      timestamp: new Date(),
    };
    setMessages(prev => [...prev, completeMessage]);
    setIsOnboardingComplete(true);
  };

  const handleContinueToProfile = async () => {
    try {
      // Recalculate priorities based on conversation
      await onboardingService.recalculatePriorities();
      // Navigate to priorities screen to show personalized task list
      navigation.replace('AIOnboardingPriorities');
    } catch (error) {
      logger.error('Failed to calculate priorities:', error);
      // Navigate to priorities screen anyway - it will show default priorities
      navigation.replace('AIOnboardingPriorities');
    }
  };

  const handlePause = () => {
    Alert.alert(
      'Pause Onboarding',
      'You can resume anytime from where you left off.',
      [
        {
          text: 'Cancel',
          style: 'cancel',
        },
        {
          text: 'Pause',
          onPress: async () => {
            try {
              analytics.track(AnalyticsEvent.AiOnboardingPaused, {
                mode,
                answered: progress.current,
                total: progress.total,
              });
              await onboardingService.pauseOnboarding();
              navigation.replace('AIOnboardingPaused');
            } catch (error) {
              logger.error('Failed to pause onboarding:', error);
              Alert.alert('Error', 'Failed to pause onboarding. Please try again.');
            }
          },
        },
      ]
    );
  };

  const renderMessage = ({ item }: { item: Message }) => {
    const isOrion = item.sender === 'orion';

    return (
      <View
        style={[
          styles.messageContainer,
          isOrion ? styles.orionMessageContainer : styles.userMessageContainer,
        ]}
      >
        {isOrion && (
          <View style={styles.orionAvatarSmall}>
            <Image
              source={require('../../assets/icons/chat.png')}
              style={styles.orionAvatarImage}
            />
          </View>
        )}
        <View
          style={[
            styles.messageBubble,
            isOrion ? styles.orionBubble : styles.userBubble,
          ]}
        >
          <Text
            style={[
              styles.messageText,
              isOrion ? styles.orionMessageText : styles.userMessageText,
            ]}
          >
            {item.text}
          </Text>
        </View>
      </View>
    );
  };

  return (
    <SafeAreaView style={styles.container} edges={['top']}>
      <KeyboardAvoidingView
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        style={styles.container}
        keyboardVerticalOffset={0}
      >
        {/* Header */}
        <View style={styles.header}>
          <View style={styles.headerLeft}>
            <TouchableOpacity onPress={() => navigation.goBack()}>
              <Text style={styles.closeText}>✕</Text>
            </TouchableOpacity>
            <Text style={styles.headerTitle}>Orion's Questions</Text>
          </View>
          <TouchableOpacity onPress={handlePause}>
            <Text style={styles.menuText}>⋮</Text>
          </TouchableOpacity>
        </View>

        {/* Progress Bar */}
        <View style={styles.progressContainer}>
          <View style={styles.progressBar}>
            <View
              style={[
                styles.progressFill,
                { width: `${(progress.current / progress.total) * 100}%` },
              ]}
            />
          </View>
          <Text style={styles.progressText}>
            Question {progress.current} of {progress.total}
          </Text>
        </View>

        {/* Messages List */}
        <FlatList
          ref={flatListRef}
          data={messages}
          renderItem={renderMessage}
          keyExtractor={(item) => item.id}
          contentContainerStyle={styles.messagesList}
          onContentSizeChange={() =>
            flatListRef.current?.scrollToEnd({ animated: true })
          }
          showsVerticalScrollIndicator={false}
        />

        {/* Typing Indicator */}
        {isLoading && (
          <View style={styles.typingContainer}>
            <View style={styles.orionAvatarSmall}>
              <Image
                source={require('../../assets/icons/chat.png')}
                style={styles.orionAvatarImage}
              />
            </View>
            <View style={styles.typingBubble}>
              <ActivityIndicator size="small" color="#29BFC0" />
              <Text style={styles.typingText}>Orion is typing...</Text>
            </View>
          </View>
        )}

        {/* Input Area or Complete Button */}
        {isOnboardingComplete ? (
          <View style={styles.completeContainer}>
            <TouchableOpacity
              style={styles.continueButton}
              onPress={handleContinueToProfile}
              activeOpacity={0.8}
            >
              <Text style={styles.continueButtonText}>See Your Task Priorities</Text>
            </TouchableOpacity>
          </View>
        ) : (
          <View style={styles.inputContainer}>
            <TextInput
              style={styles.input}
              value={inputText}
              onChangeText={setInputText}
              // In a browser Enter sends and Shift+Enter breaks the line, as in
              // Orion's main chat (AIChat.web.tsx). The phone keyboard's return
              // key keeps inserting a line.
              onKeyPress={
                Platform.OS === 'web'
                  ? (e) => {
                      const native = e.nativeEvent as { key?: string; shiftKey?: boolean };
                      if (native.key === 'Enter' && !native.shiftKey) {
                        (e as unknown as { preventDefault?: () => void }).preventDefault?.();
                        handleSend();
                      }
                    }
                  : undefined
              }
              placeholder="Type your answer..."
              placeholderTextColor="#999"
              multiline
              maxLength={500}
            />
            <TouchableOpacity
              style={[
                styles.sendButton,
                inputText.trim() === '' && styles.sendButtonDisabled,
              ]}
              onPress={handleSend}
              disabled={inputText.trim() === '' || isLoading}
            >
              <Text style={styles.sendIcon}>{'>'}</Text>
            </TouchableOpacity>
          </View>
        )}
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F3F6F8',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: ms(16),
    paddingVertical: ms(12),
    backgroundColor: '#FFFFFF',
    borderBottomWidth: ms(1),
    borderBottomColor: '#E0E0E0',
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: ms(12),
  },
  closeText: {
    fontSize: ms(24, 0.3),
    color: '#666',
  },
  headerTitle: {
    fontSize: ms(18, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
  },
  menuText: {
    fontSize: ms(24, 0.3),
    color: '#666',
  },
  progressContainer: {
    paddingHorizontal: ms(16),
    paddingVertical: ms(12),
    backgroundColor: '#FFFFFF',
    borderBottomWidth: ms(1),
    borderBottomColor: '#E0E0E0',
  },
  progressBar: {
    height: s(4),
    backgroundColor: '#E0E0E0',
    borderRadius: ms(2),
    marginBottom: ms(8),
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    backgroundColor: '#29BFC0',
    borderRadius: ms(2),
  },
  progressText: {
    fontSize: ms(12, 0.3),
    color: '#666',
  },
  messagesList: {
    paddingHorizontal: ms(16),
    paddingVertical: ms(16),
    gap: ms(16),
  },
  messageContainer: {
    flexDirection: 'row',
    marginVertical: ms(4),
  },
  orionMessageContainer: {
    justifyContent: 'flex-start',
    paddingRight: ms(60),
  },
  userMessageContainer: {
    justifyContent: 'flex-end',
    paddingLeft: ms(60),
  },
  orionAvatarSmall: {
    width: s(32),
    height: s(32),
    borderRadius: ms(16),
    backgroundColor: '#29BFC0',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: ms(8),
  },
  orionAvatarImage: {
    width: s(20),
    height: s(20),
  },
  messageBubble: {
    flex: 1,
    padding: ms(12),
    borderRadius: ms(16),
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 4,
    elevation: 2,
  },
  orionBubble: {
    backgroundColor: '#E3F2FD',
    borderTopLeftRadius: ms(4),
  },
  userBubble: {
    backgroundColor: '#29BFC0',
    borderTopRightRadius: ms(4),
    marginLeft: 'auto',
  },
  messageText: {
    fontSize: ms(16, 0.3),
    lineHeight: ms(22, 0.3),
  },
  orionMessageText: {
    color: '#1C2C4F',
  },
  userMessageText: {
    color: '#FFFFFF',
  },
  typingContainer: {
    flexDirection: 'row',
    paddingHorizontal: ms(16),
    paddingVertical: ms(12),
    alignItems: 'center',
  },
  typingBubble: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#E3F2FD',
    paddingHorizontal: ms(16),
    paddingVertical: ms(12),
    borderRadius: ms(16),
    borderTopLeftRadius: ms(4),
    gap: ms(8),
  },
  typingText: {
    fontSize: ms(14, 0.3),
    color: '#666',
    fontStyle: 'italic',
  },
  inputContainer: {
    flexDirection: 'row',
    paddingHorizontal: ms(16),
    paddingVertical: ms(12),
    paddingBottom: Platform.OS === 'ios' ? 24 : 12,
    backgroundColor: '#FFFFFF',
    borderTopWidth: ms(1),
    borderTopColor: '#E0E0E0',
    alignItems: 'flex-end',
    gap: ms(12),
  },
  input: {
    flex: 1,
    backgroundColor: '#F3F6F8',
    borderRadius: ms(20),
    paddingHorizontal: ms(16),
    paddingVertical: ms(12),
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
    maxHeight: s(100),
  },
  sendButton: {
    width: s(44),
    height: s(44),
    borderRadius: ms(22),
    backgroundColor: '#29BFC0',
    justifyContent: 'center',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  sendButtonDisabled: {
    backgroundColor: '#A9A9A9',
    opacity: 0.5,
  },
  sendIcon: {
    fontSize: ms(20, 0.3),
    fontWeight: '600',
    color: '#FFFFFF',
  },
  completeContainer: {
    paddingHorizontal: ms(16),
    paddingVertical: ms(16),
    paddingBottom: Platform.OS === 'ios' ? 32 : 16,
    backgroundColor: '#FFFFFF',
    borderTopWidth: ms(1),
    borderTopColor: '#E0E0E0',
  },
  continueButton: {
    backgroundColor: '#29BFC0',
    paddingVertical: ms(16),
    borderRadius: ms(30),
    alignItems: 'center',
    shadowColor: '#29BFC0',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 4,
  },
  continueButtonText: {
    color: '#FFFFFF',
    fontSize: ms(18, 0.3),
    fontWeight: 'bold',
  },
});
